import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, BackButton, DataGrid } from '@/components';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { SystemAnnouncementsService } from '@/services/SystemAnnouncementsService';
import { useSnackbar } from 'notistack';
import { PaginationState } from '@tanstack/react-table';

type RecipientItem = {
  id: string;
  name: string;
  email: string;
  status: string;
  deliveredAt: string | null;
};

type RecipientRow = {
  original: RecipientItem;
};

const SystemAnnouncementsRecipients = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [recipients, setRecipients] = useState<RecipientItem[]>([]);
  const [stats, setStats] = useState<{
    total: number;
    pending: number;
    delivered: number;
    failed: number;
  }>({ total: 0, pending: 0, delivered: 0, failed: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const loadData = async () => {
      try {
        setIsLoading(true);

        const [statsResponse, deliveriesResponse] = await Promise.all([
          SystemAnnouncementsService.getAnnouncementStatistics(id),
          SystemAnnouncementsService.listAnnouncementDeliveries(id, {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: searchQuery.trim() || undefined,
          }),
        ]);

        if (cancelled) return;

        setStats(statsResponse);

        const data = (deliveriesResponse?.data ?? []) as RecipientItem[];
        setRecipients(data);
        setTotalItems(
          deliveriesResponse.totalItems ??
            deliveriesResponse.total ??
            (Array.isArray(data) ? data.length : 0)
        );
        setTotalPages(
          deliveriesResponse.totalPages ?? deliveriesResponse.total_pages
        );
      } catch (error) {
        const e = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load announcement recipients';
        enqueueSnackbar(msg, { variant: 'error' });
        setRecipients([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [
    id,
    enqueueSnackbar,
    pagination.pageIndex,
    pagination.pageSize,
    searchQuery,
  ]);

  const getStatusBadge = (status: string) => {
    const baseClasses =
      'inline-flex items-center px-2.5 py-1 rounded text-xs font-medium';

    switch (status) {
      case 'delivered':
        return `${baseClasses} bg-green-50 dark:bg-green-900 contrast:bg-green-900 text-green-700 dark:text-green-200 contrast:text-green-100 border border-green-200 dark:border-green-800 contrast:border-green-800`;
      case 'pending':
        return `${baseClasses} bg-amber-50 dark:bg-amber-900 contrast:bg-amber-900 text-amber-700 dark:text-amber-200 contrast:text-amber-100 border border-amber-200 dark:border-amber-800 contrast:border-amber-800`;
      case 'failed':
        return `${baseClasses} bg-red-50 dark:bg-red-900 contrast:bg-red-900 text-red-700 dark:text-red-200 contrast:text-red-100 border border-red-200 dark:border-red-800 contrast:border-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-700 border border-gray-200`;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: { row: RecipientRow }) => (
          <span>{row.original.name || '-'}</span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        header: 'Email',
        accessorKey: 'email',
        cell: ({ row }: { row: RecipientRow }) => (
          <span>{row.original.email || '-'}</span>
        ),
        meta: { className: 'min-w-[220px]' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }: { row: RecipientRow }) => (
          <span className={getStatusBadge(row.original.status)}>
            {row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        header: 'Delivered At',
        accessorKey: 'deliveredAt',
        cell: ({ row }: { row: RecipientRow }) => (
          <span className="text-sm text-gray-600">
            {formatDateTime(row.original.deliveredAt)}
          </span>
        ),
        meta: { className: 'min-w-[160px]' },
      },
    ],
    []
  );

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';

    const trimmed = dateString.trim();
    if (!trimmed) return '-';

    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
      return trimmed;
    }

    const pad = (value: number) => value.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <div className="flex gap-2 items-center">
              <BackButton customBackPath="/system-announcements" />
              <div>
                <ToolbarPageTitle text="Announcement Recipients" />
                <ToolbarDescription>
                  View delivery status and recipient information for this
                  announcement.
                </ToolbarDescription>
              </div>
            </div>
          </ToolbarHeading>
        </Toolbar>
      </Container>

      <Container>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <div className="card p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-xs text-gray-600 mt-1">Total</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-bold text-amber-600">
              {stats.pending}
            </div>
            <div className="text-xs text-gray-600 mt-1">Pending</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-bold text-green-600">
              {stats.delivered}
            </div>
            <div className="text-xs text-gray-600 mt-1">Delivered</div>
          </div>
          <div className="card p-3 text-center">
            <div className="text-xl font-bold text-red-600">{stats.failed}</div>
            <div className="text-xs text-gray-600 mt-1">Failed</div>
          </div>
        </div>

        {/* Recipients Table */}
        <div className="card card-grid">
          <div className="card-header flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex-shrink-0">
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center"
                  htmlFor="search-recipients"
                  aria-label="Search recipients by name or email"
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    id="search-recipients"
                    placeholder="Search by name or email"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                    aria-label="Search recipients"
                  />
                </label>
              </div>

              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                  onClick={() => {
                    setSearchQuery('');
                  }}
                  aria-label="Clear all filters"
                >
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="recipients-grid-wrapper">
              <DataGrid
                columns={columns}
                data={recipients}
                rowSelect={false}
                serverSide={true}
                loading={isLoading}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems: totalItems ?? 0,
                  totalPages,
                  setPagination,
                  info: '{from} - {to} of {count}',
                  sizes: [10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                aria-label="announcement recipients table"
              />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export { SystemAnnouncementsRecipients };
